import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { LANGS } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDraft = () => ({
  id: null,
  date: "",
  translations: LANGS.reduce((acc, l) => ({ ...acc, [l.code]: { tag: "", text: "" } }), {}),
});

export default function UpdatesManager({ secret }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const auth = { headers: { "X-Admin-Secret": secret } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/updates`);
      setUpdates(res.data || []);
    } catch { setError("Could not load updates."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const startAdd = () => { setDraft(emptyDraft()); setActiveLang("en"); setError(""); };
  const startEdit = (u) => {
    const base = emptyDraft();
    const translations = { ...base.translations };
    Object.keys(u.translations || {}).forEach((k) => {
      translations[k] = { tag: u.translations[k]?.tag || "", text: u.translations[k]?.text || "" };
    });
    setDraft({ id: u.id, date: u.date, translations });
    setActiveLang("en");
    setError("");
  };

  const setField = (field, value) =>
    setDraft((d) => ({ ...d, translations: { ...d.translations, [activeLang]: { ...d.translations[activeLang], [field]: value } } }));

  const save = async () => {
    if (!draft.date.trim() || !draft.translations.en.tag.trim() || !draft.translations.en.text.trim()) {
      setError("Date and the English tag + text are required.");
      return;
    }
    setSaving(true);
    setError("");
    // Only keep languages that actually have content.
    const translations = {};
    Object.entries(draft.translations).forEach(([k, v]) => {
      if (v.tag.trim() || v.text.trim()) translations[k] = { tag: v.tag.trim(), text: v.text.trim() };
    });
    const payload = { date: draft.date.trim(), translations };
    try {
      if (draft.id) await axios.put(`${API}/updates/${draft.id}`, payload, auth);
      else await axios.post(`${API}/updates`, payload, auth);
      setDraft(null);
      await load();
    } catch { setError("Save failed. Check the admin secret and try again."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try { await axios.delete(`${API}/updates/${id}`, auth); await load(); }
    catch { setError("Delete failed."); }
  };

  const langHasContent = (code) => draft && (draft.translations[code]?.tag || draft.translations[code]?.text);

  return (
    <div data-testid="updates-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">Updates feed</h1>
          <p className="mt-2 text-[var(--muted-ink)]" data-testid="updates-count">{updates.length} published</p>
        </div>
        {!draft && (
          <button onClick={startAdd} data-testid="add-update-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-xs font-semibold uppercase tracking-wide hover:opacity-90">
            <Plus className="h-4 w-4" /> Add update
          </button>
        )}
      </div>

      {error && <p className="mt-4 text-sm text-[var(--ink)] font-medium" data-testid="updates-error">{error}</p>}

      {draft && (
        <div className="mt-8 border border-[var(--hairline)] rounded-[4px] p-6 sm:p-8" data-testid="update-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">{draft.id ? "Edit update" : "New update"}</h2>
            <button onClick={() => setDraft(null)} data-testid="cancel-update-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--muted-ink)] hover:text-[var(--ink)]">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>

          <label className="block mt-6 text-xs uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold">Date label</label>
          <input
            data-testid="update-date-input"
            value={draft.date}
            onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
            placeholder="e.g. 07 / 2026"
            className="mt-2 w-full max-w-xs bg-transparent border-b border-[var(--hairline)] py-2 text-lg focus:outline-none focus:border-[var(--ink)]"
          />

          <div className="mt-8 flex flex-wrap gap-2">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`update-lang-tab-${l.code}`}
                onClick={() => setActiveLang(l.code)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${activeLang === l.code ? "bg-[var(--ink)] text-[var(--page)] border-[var(--ink)]" : "border-[var(--hairline)] hover:bg-[var(--ink)]/5"}`}
              >
                {l.label}
                {langHasContent(l.code) && <Check className="h-3 w-3" />}
              </button>
            ))}
          </div>

          <div className="mt-5" dir={activeLang === "ar" ? "rtl" : "ltr"}>
            <label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold">
              Tag {activeLang === "en" && <span className="text-[var(--ink)]">*</span>}
            </label>
            <input
              data-testid="update-tag-input"
              value={draft.translations[activeLang]?.tag || ""}
              onChange={(e) => setField("tag", e.target.value)}
              placeholder="e.g. Tax"
              className="mt-2 w-full max-w-xs bg-transparent border-b border-[var(--hairline)] py-2 text-base focus:outline-none focus:border-[var(--ink)]"
            />
            <label className="block mt-5 text-xs uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold">
              Text {activeLang === "en" && <span className="text-[var(--ink)]">*</span>}
            </label>
            <textarea
              data-testid="update-text-input"
              value={draft.translations[activeLang]?.text || ""}
              onChange={(e) => setField("text", e.target.value)}
              rows={3}
              placeholder="Short update sentence…"
              className="mt-2 w-full bg-transparent border border-[var(--hairline)] rounded-[3px] p-3 text-base focus:outline-none focus:border-[var(--ink)]"
            />
            <p className="mt-2 text-xs text-[var(--muted-ink)] italic">English is required and is used as the fallback. Other languages are optional.</p>
          </div>

          <button onClick={save} disabled={saving} data-testid="save-update-btn" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-sm font-semibold uppercase tracking-wide hover:opacity-90 disabled:opacity-50">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save update"}
          </button>
        </div>
      )}

      <div className="mt-8 border border-[var(--hairline)] rounded-[4px] divide-y divide-[var(--hairline)]" data-testid="updates-list">
        {loading ? (
          <div className="px-4 py-10 text-center text-[var(--muted-ink)]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : updates.length === 0 ? (
          <div className="px-4 py-10 text-center text-[var(--muted-ink)]">No updates yet. Add your first one.</div>
        ) : updates.map((u) => (
          <div key={u.id} data-testid={`update-row-${u.id}`} className="flex items-start justify-between gap-4 px-4 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold tabular-nums text-[var(--muted-ink)]">{u.date}</span>
                <span className="inline-block border border-[var(--hairline)] rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{u.translations?.en?.tag || "—"}</span>
                <span className="text-[10px] text-[var(--muted-ink)]">{Object.keys(u.translations || {}).length} langs</span>
              </div>
              <p className="mt-1.5 text-sm leading-relaxed">{u.translations?.en?.text || "—"}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => startEdit(u)} data-testid={`edit-update-${u.id}`} aria-label="Edit" className="inline-flex items-center justify-center h-8 w-8 rounded-[3px] border border-[var(--hairline)] hover:bg-[var(--ink)]/5">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(u.id)} data-testid={`delete-update-${u.id}`} aria-label="Delete" className="inline-flex items-center justify-center h-8 w-8 rounded-[3px] border border-[var(--hairline)] hover:bg-[var(--ink)]/5">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
